/**
 * FASE 20.1 — DATA TOOL CONTRACTS
 *
 * Read-only tool definitions that Kevia may execute against Supabase.
 * Each tool declares what it reads, who can call it, and how it should be cached.
 *
 * INVARIANT: these tools NEVER write. NEVER raw SQL. NEVER select("*").
 */

/** @typedef {{ district_id?: string, church_id?: string, entity_id?: string, date_range?: string }} ToolFilters */

/**
 * @typedef {Object} DataToolSchema
 * @property {string}   id
 * @property {string}   description         — human-readable, also used in hints
 * @property {string}   entityScope         — "district" | "church" | "entity" | "user"
 * @property {string[]} requiredRole        — roles that can call this tool; ["*"] = any
 * @property {string[]} allowedFilters      — which ToolFilters keys are accepted
 * @property {number}   maxRows
 * @property {boolean}  offlineCompatible   — can return cached data when offline
 * @property {number}   cacheTTL            — ms; 0 = no cache
 */

export const DATA_TOOLS = Object.freeze({

  get_today_visits: {
    id:               "get_today_visits",
    description:      "Visitas programadas para hoy en tu iglesia o distrito",
    entityScope:      "church",
    requiredRole:     ["*"],
    allowedFilters:   ["district_id", "church_id"],
    maxRows:          25,
    offlineCompatible: false,
    cacheTTL:         3 * 60_000,   // 3 min
  },

  get_upcoming_events: {
    id:               "get_upcoming_events",
    description:      "Próximos eventos en los siguientes 7 días",
    entityScope:      "district",
    requiredRole:     ["*"],
    allowedFilters:   ["district_id", "church_id"],
    maxRows:          15,
    offlineCompatible: false,
    cacheTTL:         5 * 60_000,   // 5 min
  },

  get_family_overview: {
    id:               "get_family_overview",
    description:      "Resumen pastoral de una familia específica",
    entityScope:      "entity",
    requiredRole:     ["*"],
    allowedFilters:   ["entity_id"],
    maxRows:          1,
    offlineCompatible: false,
    cacheTTL:         2 * 60_000,   // 2 min
  },

  get_recent_prayers: {
    id:               "get_recent_prayers",
    description:      "Solicitudes de oración activas de los últimos 14 días",
    entityScope:      "church",
    requiredRole:     ["*"],
    allowedFilters:   ["district_id", "church_id"],
    maxRows:          20,
    offlineCompatible: false,
    cacheTTL:         5 * 60_000,
  },

  get_people_needing_followup: {
    id:               "get_people_needing_followup",
    description:      "Personas sin visita reciente que necesitan seguimiento pastoral",
    entityScope:      "church",
    requiredRole:     ["coordinator", "district_pastor", "admin"],
    allowedFilters:   ["district_id", "church_id"],
    maxRows:          20,
    offlineCompatible: false,
    cacheTTL:         10 * 60_000,  // 10 min — changes infrequently
  },

  get_church_activity_summary: {
    id:               "get_church_activity_summary",
    description:      "Resumen de actividad pastoral reciente de la iglesia",
    entityScope:      "church",
    requiredRole:     ["*"],
    allowedFilters:   ["church_id", "district_id"],
    maxRows:          10,
    offlineCompatible: false,
    cacheTTL:         10 * 60_000,
  },

  get_member_status: {
    id:               "get_member_status",
    description:      "Distribución de estados de membresía (bautizados, catecúmenos, simpatizantes)",
    entityScope:      "church",
    requiredRole:     ["coordinator", "district_pastor", "admin"],
    allowedFilters:   ["church_id", "district_id"],
    maxRows:          25,
    offlineCompatible: false,
    cacheTTL:         15 * 60_000,  // 15 min — changes rarely
  },

  get_visit_timeline: {
    id:               "get_visit_timeline",
    description:      "Historial de visitas recientes para una entidad (familia o persona)",
    entityScope:      "entity",
    requiredRole:     ["*"],
    allowedFilters:   ["entity_id", "church_id", "district_id"],
    maxRows:          10,
    offlineCompatible: false,
    cacheTTL:         3 * 60_000,
  },

  get_dashboard_snapshot: {
    id:               "get_dashboard_snapshot",
    description:      "Resumen ejecutivo: visitas de hoy, próximos eventos, oraciones activas",
    entityScope:      "district",
    requiredRole:     ["*"],
    allowedFilters:   ["district_id", "church_id"],
    maxRows:          25,
    offlineCompatible: false,
    cacheTTL:         3 * 60_000,
  },

  get_next_visit: {
    id:               "get_next_visit",
    description:      "La próxima visita pastoral pendiente (para preguntas sobre 'próxima visita')",
    entityScope:      "church",
    requiredRole:     ["*"],
    allowedFilters:   ["district_id", "church_id"],
    maxRows:          3,
    offlineCompatible: false,
    cacheTTL:         2 * 60_000,
  },

  get_upcoming_visits: {
    id:               "get_upcoming_visits",
    description:      "Visitas pendientes en los próximos 7 días",
    entityScope:      "church",
    requiredRole:     ["*"],
    allowedFilters:   ["district_id", "church_id"],
    maxRows:          15,
    offlineCompatible: false,
    cacheTTL:         3 * 60_000,
  },

  get_week_schedule: {
    id:               "get_week_schedule",
    description:      "Agenda semanal: visitas pendientes y eventos de los próximos 7 días",
    entityScope:      "district",
    requiredRole:     ["*"],
    allowedFilters:   ["district_id", "church_id"],
    maxRows:          20,
    offlineCompatible: false,
    cacheTTL:         3 * 60_000,
  },

  get_upcoming_preachings: {
    id:               "get_upcoming_preachings",
    description:      "Predicaciones programadas en el roster publicado para las próximas 2 semanas",
    entityScope:      "district",
    requiredRole:     ["coordinator", "district_pastor", "admin"],
    allowedFilters:   ["district_id"],
    maxRows:          8,
    offlineCompatible: false,
    cacheTTL:         10 * 60_000,
  },

});

export function getDataToolById(id) {
  return DATA_TOOLS[id] ?? null;
}

export function getAllDataTools() {
  return Object.values(DATA_TOOLS);
}
